import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  styled,
  IconButton,
  CircularProgress,
  AlertColor,
  useMediaQuery,
  Theme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  EmptyRequest,
  HomeInteractionsContainer,
  InteractionTextContainer,
  PatientContainer,
  RequestContainer,
  RequestsListContainer,
  Row,
  Search,
  StyledInfoIcon,
  StyledShareIcon,
  WhiteBackgroundBody,
} from "./styled";
import {
  DoctorDocumentReadinessModal,
  Body,
  HomeActionButton,
  NewDocumentIcon,
  ContentContainer,
  SnackBar,
  Modal,
} from "@components";
import {
  PendingFeaAuthAlert,
  RequestCard,
  SignatureMigrationAlert,
  useSignatureMigrationBanner,
  RequestsSkeleton,
  SidebarMenu,
} from "./components";
import { AuthFeedbackModal } from "@components/Modals/AuthFeedbackModal";
import { AuthStep, Request } from "@interfaces";
import { useAuth, useDoctorContext } from "@contexts";
import {
  useDebounce,
  useRequestsSearch,
  useDoctorDocumentReadiness,
} from "@hooks";
import type { RequestSearchResponse } from "@hooks";
import { removeFromLocalStorage, shareDoctorLink } from "@utils";
import { useClearAllInteractionsStores } from "@hooks/useInteractionsStore";
import { StyledErrorIcon, StyledSuccessIcon } from "@styles/styled";
import { identifyOpenReplaySession } from "@utils/openreplay";
import type { InfiniteData } from "@tanstack/react-query";

const FilterListButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isFilterActive",
})<{ isFilterActive: boolean }>(({ isFilterActive, theme }) => ({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  ...(!isFilterActive && {
    color: theme.palette.icon.primary,
  }),
  ...(isFilterActive && {
    color: theme.palette.icon.contrast,
    backgroundColor: `${theme.palette.primary.main} !important`,
  }),
}));

const FilterText = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginBottom: "8px",
}));

export function Requests() {
  const navigate = useNavigate();
  const doctor = useDoctorContext();
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const [showCopiedLinkModal, setShowCopiedLinkModal] = useState(false);
  const [snackBarState, setSnackBarState] = useState({
    open: false,
    type: "success" as AlertColor,
  });

  useClearAllInteractionsStores();

  useEffect(() => {
    removeFromLocalStorage("requestType");
    removeFromLocalStorage("compoundedPrescriptionDetails");
  }, []);

  const {
    authChallengeFeedback: { message: authFeedback, authStep },
  } = useAuth();
  const feedbackIcon = (authStep: AuthStep) => {
    switch (authStep) {
      case AuthStep.SUCCESS:
        return <StyledSuccessIcon />;
      case AuthStep.ERROR:
        return <StyledErrorIcon />;
      default:
        return <CircularProgress size={24} color="primary" />;
    }
  };

  const { ensureReady, modalProps } = useDoctorDocumentReadiness();

  const [search, setSearch] = useState("");

  const [isFilterActive, setFilterIsActive] = useState(false);
  const statusFilter = isFilterActive ? "pending" : "active";
  const debouncedSearch = useDebounce(search, 1000);
  const {
    data: paginatedRequests,
    isLoading: isLoadingRequests,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useRequestsSearch({
    searchTerm: debouncedSearch,
    status: statusFilter,
  });

  const infiniteRequests = paginatedRequests as
    | InfiniteData<RequestSearchResponse>
    | undefined;

  const requests = useMemo(() => {
    if (!infiniteRequests?.pages) return [];
    return infiniteRequests.pages.flatMap((page) => page.items);
  }, [infiniteRequests]);

  const groupedRequestsByDate = useMemo(() => {
    return requests.reduce<Record<string, Request[]>>(
      (all: Record<string, Request[]>, request: Request) => {
        const isoDate = new Date(request.requestedAt).toISOString();
        const date = isoDate.split("T")[0];
        return {
          ...all,
          [date]: [...(all[date] || []), request],
        };
      },
      {},
    );
  }, [requests]);

  const dates = useMemo(() => {
    return Object.keys(groupedRequestsByDate).sort().reverse();
  }, [groupedRequestsByDate]);

  const isInitialLoading = isLoadingRequests && requests.length === 0;
  const isLoadingOrFetchingRequests =
    (isLoadingRequests || isFetching) && requests.length > 0;
  const shouldShowFeaAlert =
    !isInitialLoading && doctor.isFeaAuthenticated === false;
  const signatureMigrationBanner = useSignatureMigrationBanner();
  const migrationBanner =
    !isInitialLoading && !shouldShowFeaAlert ? signatureMigrationBanner : null;
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasNextPage) return;
    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const emptyStateMessage = useMemo(() => {
    if (search.trim().length > 0) {
      return "No se encontraron solicitudes";
    }
    if (isFilterActive) {
      return "No tienes solicitudes pendientes";
    }
    return "No tienes solicitudes";
  }, [isFilterActive, search]);

  const onShare = useCallback(async () => {
    ensureReady(async () => {
      await shareDoctorLink({
        doctorCode: doctor.code,
        setShowCopiedLinkModal,
        setSnackBarState,
        isDesktop,
      });
    });
  }, [ensureReady, doctor.code, isDesktop]);

  const onNewPrescription = useCallback(() => {
    ensureReady(() => {
      navigate(`/app/nuevo-documento`);
    });
  }, [ensureReady, navigate]);

  useEffect(() => {
    identifyOpenReplaySession(doctor);
  }, [doctor]);

  return (
    <>
      <SidebarMenu />
      <ContentContainer disableGutters>
        <Body>
          <RequestContainer>
            <WhiteBackgroundBody>
              <HomeInteractionsContainer>
                <HomeActionButton onClick={onShare} icon={<StyledShareIcon />}>
                  <InteractionTextContainer>
                    Formulario para pacientes
                  </InteractionTextContainer>
                </HomeActionButton>
                <HomeActionButton
                  onClick={onNewPrescription}
                  icon={<NewDocumentIcon />}
                >
                  <InteractionTextContainer>
                    Nuevo documento
                  </InteractionTextContainer>
                </HomeActionButton>
              </HomeInteractionsContainer>
            </WhiteBackgroundBody>

            <Row>
              <Search
                placeholder="Buscar por nombre o RUT"
                searchText={search}
                setSearchText={setSearch}
                showSuggestions={false}
                obscureInTracker
              />
              <FilterListButton
                onClick={() => setFilterIsActive((prev) => !prev)}
                isFilterActive={isFilterActive}
              >
                <FilterListIcon />
              </FilterListButton>
            </Row>
            {isFilterActive && <FilterText>Filtrado por pendientes</FilterText>}
            {shouldShowFeaAlert && <PendingFeaAuthAlert />}
            {migrationBanner && (
              <SignatureMigrationAlert banner={migrationBanner} />
            )}
            <RequestsListContainer>
              {!isInitialLoading &&
                dates.length > 0 &&
                dates.map((date) => {
                  return (
                    <Fragment key={date}>
                      {groupedRequestsByDate[date]
                        .sort(
                          (a: Request, b: Request) =>
                            b.requestedAt - a.requestedAt,
                        )
                        .map((request: Request) => (
                          <RequestCard
                            key={request.id}
                            id={request.id}
                            name={request.patient.name}
                            lastName={`${request.patient.firstSurname} ${request.patient.secondSurname}`}
                            rut={request.patient.rut || ""}
                            passport={request.patient.passport || ""}
                            requestedAt={new Date(request.requestedAt)}
                            status={request.status}
                            requestType={request.documentType}
                            isLoadingRequests={isLoadingOrFetchingRequests}
                            managementSummary={request.managementSummary}
                          />
                        ))}
                    </Fragment>
                  );
                })}
              {!isInitialLoading && dates.length === 0 && (
                <EmptyRequest>
                  <StyledInfoIcon />
                  {emptyStateMessage}
                </EmptyRequest>
              )}
              {isInitialLoading && <RequestsSkeleton />}
              {hasNextPage && (
                <>
                  <div ref={loadMoreRef} style={{ height: 1 }} />
                  {isFetchingNextPage && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "16px 0",
                      }}
                    >
                      <CircularProgress size={24} color="primary" />
                    </div>
                  )}
                </>
              )}
            </RequestsListContainer>
          </RequestContainer>
          <PatientContainer>
            {/* TODO: Uncomment this when the "patients" is implemented */}
            {/* <PatientsListComponent requests={requests} /> */}
          </PatientContainer>

          <Modal
            title="Link copiado"
            content="Pégalo en WhatsApp o en un correo para enviarlo al paciente."
            isOpen={showCopiedLinkModal}
            onClose={() => setShowCopiedLinkModal(false)}
            buttonProps={{
              buttonText: "Aceptar",
              onClick: () => {
                setShowCopiedLinkModal(false);
              },
            }}
          />

          <SnackBar
            snackBarState={snackBarState}
            onClose={() => setSnackBarState({ ...snackBarState, open: false })}
            alerts={{
              success: "Link copiado, compártelo con tu paciente",
              error: "Ocurrió un error al copiar el link",
            }}
            style={{ margin: "0px" }}
          />
          <DoctorDocumentReadinessModal
            {...modalProps}
          />
          {authFeedback && (
            <AuthFeedbackModal
              isOpen={true}
              authFeedback={authFeedback}
              authStep={authStep}
              feedbackIcon={feedbackIcon}
            />
          )}
        </Body>
      </ContentContainer>
    </>
  );
}

